import React, { useState, useEffect } from 'react';
import { X, Search, AlertTriangle, Check, User, ChevronLeft, ChevronRight } from 'lucide-react';
import {getUsers} from "../api/user/user.api";

const TestSendModal = ({ isOpen, onClose, onSend }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);

    // [New] 서버 사이드 페이징을 위한 State
    const [users, setUsers] = useState([]); // 실제 유저 데이터
    const [page, setPage] = useState(1);    // 현재 페이지 (1부터 시작)
    const [totalPages, setTotalPages] = useState(1); // 전체 페이지 수
    const [loading, setLoading] = useState(false);

    const PAGE_SIZE = 4; // 한 페이지당 보여줄 개수 (요청하신 코드의 4 반영)

    // [New] 데이터 조회 (검색어 또는 페이지 변경 시 실행)
    useEffect(() => {
        if (!isOpen) return; // 모달이 닫혀있으면 요청 안함

        setLoading(true);
        getUsers(searchTerm, null, null, true, page - 1, PAGE_SIZE)
            .then(({data}) => {
                setUsers(data.kakaoUsers);
                setTotalPages(data.totalCount);
            })
            .catch((err) => {
                console.error("고객 리스트 조회 실패:", err);
                setUsers([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [searchTerm, page, isOpen]);

    // 검색 핸들러 (검색어 변경 시 페이지 초기화)
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPage(1); // 검색어가 바뀌면 1페이지로 리셋
    };

    // 체크박스 토글
    const toggleSelect = (user) => {
        const id = user.id || user.userId; // ID 필드명 확인 필요
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]
        );
    };

    // 전체 선택/해제 (현재 페이지 기준)
    const toggleAllCurrentPage = () => {
        const currentPageIds = users.map(u => u.id || u.userId);
        const isAllSelected = currentPageIds.every(id => selectedIds.includes(id));

        if (isAllSelected) {
            // 현재 페이지 아이템들만 선택 해제
            setSelectedIds(prev => prev.filter(id => !currentPageIds.includes(id)));
        } else {
            // 현재 페이지 아이템들 추가 (중복 제거)
            const newIds = new Set([...selectedIds, ...currentPageIds]);
            setSelectedIds([...newIds]);
        }
    };

    // 전송 핸들러
    const handleSendClick = () => {
        if (selectedIds.length === 0) return alert("발송할 대상을 선택해주세요.");

        onSend(selectedIds);

        onClose();
        setSelectedIds([]);
        setSearchTerm("");
        setPage(1);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fadeIn p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center flex-shrink-0">
                    <h3 className="text-lg font-bold text-gray-800">테스트 발송</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-hidden flex flex-col p-6">

                    {/* 안내 문구 */}
                    <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 mb-5 flex items-start flex-shrink-0">
                        <AlertTriangle size={18} className="text-orange-500 mr-3 mt-0.5 flex-shrink-0" />
                        <ul className="text-xs text-orange-800 space-y-1 list-disc pl-1 font-medium">
                            <li>테스트 발송도 비용이 발생됩니다.</li>
                            <li>선택된 대상이 메시지 차단 등으로 발송이 불가할 수 있습니다.</li>
                            <li>하나의 대상으로 1시간에 최대 3번까지 발송할 수 있습니다.</li>
                        </ul>
                    </div>

                    {/* 검색창 */}
                    <div className="relative mb-4 flex-shrink-0">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                            placeholder="이름 또는 전화번호 검색"
                            value={searchTerm}
                            onChange={handleSearchChange} // 변경된 핸들러 사용
                        />
                    </div>

                    {/* 리스트 헤더 (전체선택) */}
                    <div className="flex items-center justify-between pb-2 border-b border-gray-100 mb-2 flex-shrink-0">
                        <div className="flex items-center">
                            <button
                                onClick={toggleAllCurrentPage}
                                className={`w-5 h-5 rounded border flex items-center justify-center mr-2 transition-colors ${
                                    users.length > 0 && users.every(u => selectedIds.includes(u.id || u.userId))
                                        ? 'bg-indigo-600 border-indigo-600 text-white'
                                        : 'bg-white border-gray-300 text-transparent'
                                }`}
                            >
                                <Check size={14} strokeWidth={3} />
                            </button>
                            <span className="text-sm font-bold text-gray-700">현재 페이지 전체 선택</span>
                        </div>
                        <span className="text-xs text-gray-500">
                            총 {selectedIds.length}명 선택됨
                        </span>
                    </div>

                    {/* 고객 리스트 (스크롤 영역) */}
                    <div className="flex-1 overflow-y-auto -mx-2 px-2 scrollbar-thin scrollbar-thumb-gray-200 min-h-[200px]">
                        {loading ? (
                            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-2"></div>
                                <span className="text-xs">불러오는 중...</span>
                            </div>
                        ) : users.length > 0 ? (
                            <div className="space-y-1">
                                {users.map((user, idx) => {
                                    const userId = user.id || user.userId;
                                    const isSelected = selectedIds.includes(userId);
                                    return (
                                        <div
                                            key={userId || idx}
                                            onClick={() => toggleSelect(user)}
                                            className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors border ${
                                                isSelected
                                                    ? 'bg-indigo-50 border-indigo-200'
                                                    : 'bg-white border-transparent hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center mr-3 transition-colors ${
                                                isSelected
                                                    ? 'bg-indigo-600 border-indigo-600 text-white'
                                                    : 'bg-white border-gray-300 text-transparent'
                                            }`}>
                                                <Check size={14} strokeWidth={3} />
                                            </div>
                                            <div className="flex items-center flex-1">
                                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 mr-3 overflow-hidden">
                                                    {/* 프로필 이미지가 있다면 표시, 없으면 아이콘 */}
                                                    {user.profileImage ? <img src={user.profileImage} alt="" className="w-full h-full object-cover"/> : <User size={16} />}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-bold text-gray-800">{user.name || '이름 없음'}</div>
                                                    <div className="text-xs text-gray-500">{user.phone || user.phoneNumber || '번호 없음'}</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                                <Search size={32} className="mb-2 opacity-50"/>
                                <span className="text-sm">검색 결과가 없습니다.</span>
                            </div>
                        )}
                    </div>

                    {/* [New] 페이지네이션 UI */}
                    <div className="flex items-center justify-center gap-4 pt-4 border-t border-gray-100 mt-2">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600 transition-colors"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <span className="text-sm font-medium text-gray-600">
                            {page} / {totalPages}
                        </span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600 transition-colors"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>

                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 flex gap-3 flex-shrink-0 bg-white">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 px-4 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                    >
                        취소
                    </button>
                    <button
                        onClick={handleSendClick}
                        disabled={selectedIds.length === 0}
                        className={`flex-1 py-3 px-4 rounded-xl font-bold shadow-md transform transition active:scale-95 flex items-center justify-center
                            ${selectedIds.length > 0
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'}
                        `}
                    >
                        {selectedIds.length}명에게 발송하기
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TestSendModal;